import { h, formatDate, formatDuration, type Child } from '../ui/dom.js';
import { card, metric, button, editionStatusBadge, readinessBadge, emptyState, table } from '../ui/components.js';
import { api } from '../api.js';
import { can, getUser } from '../store.js';
import type { Page, PageContext } from '../page.js';
import type { ChecklistView, Edition, EditionMinistration } from '../types.js';

export async function dashboardPage(context: PageContext): Promise<Page> {
  const user = getUser();
  const firstName = user?.name.split(' ')[0] ?? '';

  if (can.isMinistrador()) {
    return ministerDashboard(context, firstName);
  }
  return managementDashboard(context, firstName);
}

/* ---------- Painel do ministrador ----------------------------------------- */

async function ministerDashboard(context: PageContext, firstName: string): Promise<Page> {
  const { items } = await api.get<{ items: EditionMinistration[] }>('/api/my/ministrations');

  const upcoming = items.filter((item) => item.scheduledEnd && new Date(item.scheduledEnd) >= new Date());
  const homologadas = items.filter((item) => item.readiness === 'HOMOLOGADO').length;

  const rows = items.map((item) => [
    h(
      'div',
      { class: 'stack stack--sm' },
      h('div', { style: { fontWeight: '600' }, text: item.title }),
      h('div', { class: 'muted small', text: item.editionName ?? '—' }),
    ),
    h('div', { class: 'mono', text: `${formatDate(item.scheduledStart)} · ${item.scheduledStart ? new Date(item.scheduledStart).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false }) : '—'}` }),
    h('div', { text: formatDuration(item.durationMin) }),
    h('div', null, readinessBadge(item.readiness)),
    h(
      'div',
      { class: 'table__actions' },
      button('Modo Púlpito', {
        size: 'sm',
        variant: 'subtle',
        onClick: () => context.navigate(`/ministracoes/${item.id}/pulpito`),
      }),
      button('Material', {
        size: 'sm',
        variant: 'ghost',
        onClick: () => context.navigate(`/ministracoes/${item.id}`),
      }),
    ),
  ]);

  return {
    title: `Bem-vindo, ${firstName}`,
    subtitle: 'Suas ministrações, roteiros e materiais de apoio.',
    actions: [
      button('Minhas ministrações', {
        variant: 'ghost',
        size: 'sm',
        onClick: () => context.navigate('/minhas-ministracoes'),
      }),
    ],
    body: [
      h(
        'div',
        { class: 'grid grid--3' },
        metric('Ministrações escaladas', items.length, 'accent'),
        metric('Próximas', upcoming.length, 'info'),
        metric('Homologadas', homologadas, 'success'),
      ),
      card({
        title: 'Minhas ministrações',
        hint: 'Abra o Modo Púlpito para conduzir a ministração no palco.',
        body:
          items.length > 0
            ? table(['Ministração', 'Data e hora', 'Duração', 'Status', ''], rows)
            : emptyState('Você ainda não foi escalado para nenhuma ministração.', '✦'),
      }),
    ],
  };
}

/* ---------- Painel de gestao ---------------------------------------------- */

async function managementDashboard(context: PageContext, firstName: string): Promise<Page> {
  const { items: editions } = await api.get<{ items: Edition[] }>('/api/editions');

  const active = editions.filter((edition) => edition.status === 'ATIVA');
  const upcoming = editions
    .filter((edition) => new Date(edition.startDate) >= new Date())
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

  // Busca o check-list da edicao ativa / proxima para resumir o pipeline.
  const focus = active[0] ?? upcoming[0] ?? editions[0];
  let checklist: ChecklistView | null = null;

  if (focus) {
    try {
      checklist = await api.get<ChecklistView>(`/api/editions/${focus.id}/checklist`);
    } catch {
      checklist = null;
    }
  }

  const editionRows = editions.slice(0, 6).map((edition) => [
    h('div', { style: { fontWeight: '600' }, text: edition.name }),
    h('div', { class: 'muted small', text: edition.location ?? '—' }),
    h('div', { class: 'mono', text: formatDate(edition.startDate) }),
    h('div', null, editionStatusBadge(edition.status)),
    h(
      'div',
      { class: 'table__actions' },
      button('Abrir', {
        size: 'sm',
        variant: 'ghost',
        onClick: () => context.navigate(`/edicoes/${edition.id}`),
      }),
      button('Prontidão', {
        size: 'sm',
        variant: 'subtle',
        onClick: () => context.navigate(`/edicoes/${edition.id}/prontidao`),
      }),
    ),
  ]);

  const cards: Child[] = [
    h(
      'div',
      { class: 'grid grid--4' },
      metric('Edições', editions.length, 'accent'),
      metric('Edições ativas', active.length, 'info'),
      metric(
        'Ministrações',
        checklist ? checklist.totals.total : '—',
        'default',
      ),
      metric(
        'Homologadas',
        checklist ? checklist.totals.homologado : '—',
        'success',
      ),
    ),
  ];

  if (focus && checklist) {
    cards.push(
      card({
        title: `Prontidão · ${focus.name}`,
        hint: 'Pipeline de homologação da edição em foco.',
        actions: [
          button('Ver check-list', {
            variant: 'ghost',
            size: 'sm',
            onClick: () => context.navigate(`/edicoes/${focus.id}/prontidao`),
          }),
          button('Painel de apoio', {
            variant: 'subtle',
            size: 'sm',
            onClick: () => context.navigate(`/edicoes/${focus.id}/apoio`),
          }),
        ],
        body: h(
          'div',
          { class: 'stack' },
          h(
            'div',
            { class: 'row row--between' },
            h('div', { class: 'muted small', text: `${checklist.totals.progressPercent}% homologado` }),
            h('div', {
              class: 'muted small',
              text: `${checklist.totals.homologado} de ${checklist.totals.total}`,
            }),
          ),
          h('div', { class: 'progress' }, h('div', { class: 'progress__bar', style: { width: `${checklist.totals.progressPercent}%` } })),
          h(
            'div',
            { class: 'grid grid--3', style: { marginTop: '8px' } },
            h(
              'div',
              { class: 'stack stack--sm' },
              h('div', { class: 'metric__label', text: 'Pendentes' }),
              h('div', { style: { fontSize: '22px', fontWeight: '800' }, text: String(checklist.totals.pendente) }),
            ),
            h(
              'div',
              { class: 'stack stack--sm' },
              h('div', { class: 'metric__label', text: 'Em preparação' }),
              h('div', { style: { fontSize: '22px', fontWeight: '800' }, text: String(checklist.totals.emPreparacao) }),
            ),
            h(
              'div',
              { class: 'stack stack--sm' },
              h('div', { class: 'metric__label', text: 'Homologadas' }),
              h('div', { style: { fontSize: '22px', fontWeight: '800' }, text: String(checklist.totals.homologado) }),
            ),
          ),
          checklist.schedule.totalMinutes > 0
            ? h('div', {
                class: 'muted small',
                text: `Grade total: ${formatDuration(checklist.schedule.totalMinutes)}`,
              })
            : null,
        ),
      }),
    );
  }

  const quickActions: Child[] = [];
  if (can.createEditions()) {
    quickActions.push(
      button('Nova edição', { onClick: () => context.navigate('/edicoes?nova=1') }),
    );
  }
  if (can.manageTemplates()) {
    quickActions.push(
      button('Novo modelo', {
        variant: 'ghost',
        onClick: () => context.navigate('/modelos?novo=1'),
      }),
    );
  }
  if (can.manageUsers()) {
    quickActions.push(
      button('Gerenciar usuários', {
        variant: 'ghost',
        onClick: () => context.navigate('/usuarios'),
      }),
    );
  }

  cards.push(
    card({
      title: 'Edições recentes',
      hint: 'Encontros cadastrados no sistema.',
      actions: quickActions,
      body:
        editions.length > 0
          ? table(['Edição', 'Local', 'Início', 'Status', ''], editionRows)
          : emptyState('Nenhuma edição cadastrada ainda.', '▤'),
    }),
  );

  return {
    title: `Bem-vindo, ${firstName}`,
    subtitle: 'Visão geral das ministrações, escalas e prontidão das edições.',
    body: cards,
  };
}
